<script setup lang="ts">
import { passwordStrength, type PasswordStrengthLevel } from '~/utils/password'

/**
 * Password + confirmation pair for account creation, with a live strength
 * meter and a requirements checklist. Used by the normal register wizard and
 * the social-signup completion step so the password UX is identical everywhere.
 *
 * Field names default to `password` / `confirmPassword` to line up with the
 * parent UForm zod schema.
 */
const props = withDefaults(defineProps<{
  passwordName?: string
  confirmName?: string
  disabled?: boolean
}>(), {
  passwordName: 'password',
  confirmName: 'confirmPassword',
  disabled: false,
})

const password = defineModel<string>('password', { default: '' })
const confirm = defineModel<string>('confirm', { default: '' })

const { t: $t } = useI18n()

const visible = ref(false)

const strength = computed(() => passwordStrength(password.value))

const STRENGTH_BAR: Record<PasswordStrengthLevel, string> = {
  weak: 'bg-red-500',
  fair: 'bg-orange-500',
  good: 'bg-yellow-500',
  strong: 'bg-green-500',
}
const STRENGTH_TEXT: Record<PasswordStrengthLevel, string> = {
  weak: 'text-red-600 dark:text-red-400',
  fair: 'text-orange-600 dark:text-orange-400',
  good: 'text-yellow-600 dark:text-yellow-400',
  strong: 'text-green-600 dark:text-green-400',
}

const confirmDirty = computed(() => confirm.value.length > 0)
const confirmMatches = computed(() => confirmDirty.value && confirm.value === password.value)
</script>

<template>
  <div class="space-y-3">
    <UFormField :label="$t('auth.password.label')" :name="props.passwordName">
      <UInput
        v-model="password"
        :type="visible ? 'text' : 'password'"
        size="xl"
        icon="i-heroicons-lock-closed"
        :placeholder="$t('auth.password.placeholder')"
        autocomplete="new-password"
        :disabled="props.disabled"
        class="w-full"
      >
        <template #trailing>
          <UButton
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            variant="link"
            :icon="visible ? 'i-lucide-eye-off' : 'i-lucide-eye'"
            :aria-label="visible ? $t('auth.password.hide') : $t('auth.password.show')"
            @click="visible = !visible"
          />
        </template>
      </UInput>
    </UFormField>

    <!-- Strength meter -->
    <div v-if="password.length" class="space-y-1.5">
      <div class="flex items-center justify-between text-xs">
        <span class="text-gray-500 dark:text-gray-400">{{ $t('auth.password.strength') }}</span>
        <span class="font-semibold" :class="STRENGTH_TEXT[strength.level]" aria-live="polite">
          {{ $t(`auth.password.${strength.level}`) }}
        </span>
      </div>
      <div class="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          class="h-full rounded-full transition-all duration-300"
          :class="STRENGTH_BAR[strength.level]"
          :style="{ width: `${strength.percentage}%` }"
        />
      </div>
    </div>

    <!-- Requirements checklist (shared with the VM checkout) -->
    <UiPasswordRequirements :password="password" :columns="2" />

    <UFormField :label="$t('auth.password.confirmLabel')" :name="props.confirmName">
      <UInput
        v-model="confirm"
        :type="visible ? 'text' : 'password'"
        size="xl"
        icon="i-heroicons-lock-closed"
        :placeholder="$t('auth.password.confirmPlaceholder')"
        autocomplete="new-password"
        :disabled="props.disabled"
        class="w-full"
      >
        <template v-if="confirmDirty" #trailing>
          <UIcon
            :name="confirmMatches ? 'i-lucide-circle-check' : 'i-lucide-circle-x'"
            class="w-4 h-4"
            :class="confirmMatches ? 'text-green-500' : 'text-red-500'"
          />
        </template>
      </UInput>
    </UFormField>

    <p
      v-if="confirmDirty && !confirmMatches"
      class="text-xs text-red-600 dark:text-red-400"
      aria-live="polite"
    >
      {{ $t('auth.password.noMatch') }}
    </p>
  </div>
</template>
